import clsx from 'clsx';
import React from 'react';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { TreeView, TreeItem, useTreeItem } from '@mui/lab';
import { Box, Link } from '@mui/material';
import { arrayToTree } from 'performant-array-to-tree';
import { AccessibleEntity, Entity } from './types';
import { removeCookie } from './utils/removeCookie';
import { MultiOrgCookies, WorkingOrgCookie } from './hooks/useMultiOrgCookies';

interface EntityPickerTreeProps {
  entities: AccessibleEntity[] | Entity[];
}

export const EntityPickerTree = ({ entities }: EntityPickerTreeProps) => {
  const tree = arrayToTree(entities);

  return (
    <TreeView
      disableSelection
      defaultCollapseIcon={<ExpandMore sx={{ cursor: 'pointer' }} />}
      defaultExpandIcon={<ChevronRight sx={{ cursor: 'pointer' }} />}
      defaultExpanded={entities.map((e) => e.id)}
      sx={{ padding: '4px 8px' }}
    >
      {(() => {
        const renderTree = (node) => {
          const hasLink = Boolean(node.data.targetUrl);
          const hasChildren = node.children?.length > 0;
          const disabled = !node.data?.targetUrl && !hasChildren;

          return (
            <TreeItem
              key={node.data.id}
              nodeId={node.data.id}
              disabled={disabled}
              ContentComponent={React.forwardRef(
                (
                  {
                    expansionIcon,
                    nodeId,
                    displayIcon,
                    onMouseDown,
                    onClick,
                    classes,
                    className,
                    icon: iconProp,
                    ...other
                  },
                  ref,
                ) => {
                  const {
                    disabled,
                    expanded,
                    selected,
                    focused,
                    handleExpansion,
                    handleSelection,
                    preventSelection,
                  } = useTreeItem(nodeId);

                  const entity = entities.find(
                    (e) => e.id === nodeId,
                  ) as Entity;

                  const icon = iconProp || expansionIcon || displayIcon;

                  const handleMouseDown = (event) => {
                    preventSelection(event);

                    if (onMouseDown) {
                      onMouseDown(event);
                    }
                  };

                  const handleClick = (event) => {
                    handleExpansion(event);
                    handleSelection(event);

                    if (onClick) {
                      onClick(event);
                    }

                    // clear multi-org cookies when switching multi-entity
                    if (hasLink) {
                      removeCookie(MultiOrgCookies);
                      removeCookie(WorkingOrgCookie);
                    }
                  };

                  return (
                    <Box
                      component={hasLink ? Link : 'span'}
                      href={entity.targetUrl}
                      className={clsx(className, classes.root, {
                        [classes.expanded]: expanded,
                        [classes.selected]: selected,
                        [classes.focused]: focused,
                        [classes.disabled]: disabled,
                      })}
                      sx={{
                        '&&': {
                          cursor: disabled ? 'auto' : 'pointer',
                        },
                      }}
                      onClick={handleClick}
                      onMouseDown={handleMouseDown}
                      ref={ref as any}
                      {...other}
                    >
                      <div className={classes.iconContainer}>{icon}</div>
                      <Box
                        className={classes.label}
                        style={{
                          fontSize: '14px',
                          padding: '5.5px 0',
                          display: 'inline-block',
                          textDecoration: 'none',
                        }}
                      >
                        {entity.displayName || entity.name}
                      </Box>
                    </Box>
                  );
                },
              )}
            >
              {hasChildren
                ? node.children.map((node) => renderTree(node))
                : null}
            </TreeItem>
          );
        };

        return tree.map((node) => renderTree(node));
      })()}
    </TreeView>
  );
};
